import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class GreenhouseKitConfigurator extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-kit-data]').textContent);
    this.currency = this.dataset.currency || 'USD';
    this.locale = this.dataset.locale || document.documentElement.lang || 'en-US';
    this.shapeOption = Number(this.dataset.shapeOption);
    this.widthOption = Number(this.dataset.widthOption);
    this.lengthOption = Number(this.dataset.lengthOption);
    this.selectedShape = '';
    this.selectedWidth = '';
    this.selectedLength = '';
    this.selectedFanVariantId = null;
    this.selectedIntakeVariantId = null;
    this.selectedEndWall = 'none';
    this.selectedTruss = 'none';
    this.selectedCovering = 'standard';
    this.quantity = 1;
    this.bindEvents();
    this.initializeSelection();
  }

  bindEvents() {
    this.querySelectorAll('[data-shape]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectedShape = button.dataset.shape;
        this.updateLengths();
        this.update();
      });
    });
    this.querySelectorAll('[data-width]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectedWidth = button.dataset.width;
        this.updateLengths();
        this.update();
      });
    });
    this.querySelector('[data-length-select]')?.addEventListener('change', (event) => {
      this.selectedLength = event.target.value;
      this.update();
    });
    this.querySelectorAll('input[name="kit-ventilation"]').forEach((input) => {
      input.addEventListener('change', () => {
        const none = this.querySelector('input[name="kit-ventilation"][value="none"]');
        const choice = input.closest('[data-addon-kind="ventilation"]');
        if (input.value === 'none' && input.checked) {
          this.querySelectorAll('input[name="kit-ventilation"]:not([value="none"])').forEach((addon) => { addon.checked = false; });
        } else if (input.checked) {
          if (none) none.checked = false;
          if (choice?.dataset.poweredFans === 'true') this.ensureFanSelection(choice);
        }
        const hasSelectedPackage = Boolean(this.querySelector('input[name="kit-ventilation"]:not([value="none"]):checked'));
        if (!hasSelectedPackage && none) none.checked = true;
        this.update();
      });
    });
    this.querySelectorAll('[data-fan-size-selector]').forEach((select) => {
      select.addEventListener('change', () => {
        this.selectedFanVariantId = Number(select.value) || null;
        const choice = select.closest('[data-addon-kind="ventilation"]');
        const input = choice?.querySelector('input[name="kit-ventilation"]');
        if (input) {
          input.checked = true;
          const none = this.querySelector('input[name="kit-ventilation"][value="none"]');
          if (none) none.checked = false;
        }
        this.update();
      });
    });
    this.querySelectorAll('[data-intake-size-selector]').forEach((select) => {
      select.addEventListener('change', () => {
        this.selectedIntakeVariantId = Number(select.value) || null;
        const choice = select.closest('[data-addon-kind="ventilation"]');
        const input = choice?.querySelector('input[name="kit-ventilation"]');
        if (input) {
          input.checked = true;
          const none = this.querySelector('input[name="kit-ventilation"][value="none"]');
          if (none) none.checked = false;
        }
        this.update();
      });
    });
    this.querySelectorAll('input[name="kit-end-wall"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.selectedEndWall = input.value;
        this.update();
      });
    });
    this.querySelectorAll('input[name="kit-truss"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.selectedTruss = input.value;
        this.update();
      });
    });
    this.querySelectorAll('input[name="kit-covering"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.selectedCovering = input.value;
        this.update();
      });
    });
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.setQuantity(this.quantity - 1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
    this.querySelector('[data-quantity]')?.addEventListener('change', (event) => this.setQuantity(Number(event.target.value)));
    this.querySelector('[data-add-configuration]')?.addEventListener('click', (event) => this.addToCart(event));
    this.querySelectorAll('[data-media-thumb]').forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button));
    });
    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.changeMedia(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.changeMedia(1));
  }

  initializeSelection() {
    const selected = this.data.variants.find((variant) => variant.id === this.data.initialVariantId) || this.data.variants.find((variant) => variant.available) || this.data.variants[0];
    if (selected) {
      this.selectedShape = this.shapeOption >= 0 ? selected.options[this.shapeOption] || '' : '';
      this.selectedWidth = selected.options[this.widthOption] || '';
      this.selectedLength = selected.options[this.lengthOption] || '';
    }
    this.updateLengths();
    this.update();
  }

  variantsForWidth(width) {
    const minimumLength = this.dimensionNumber(width);
    return this.data.variants.filter((variant) => {
      const matchesShape = this.shapeOption < 0 || variant.options[this.shapeOption] === this.selectedShape;
      const matchesWidth = variant.options[this.widthOption] === width;
      const length = this.dimensionNumber(variant.options[this.lengthOption]);
      return matchesShape && matchesWidth && length >= minimumLength;
    });
  }

  dimensionNumber(value) {
    return Number.parseFloat(String(value || '').replace(/[^0-9.]/g, '')) || 0;
  }

  updateLengths() {
    const select = this.querySelector('[data-length-select]');
    if (!select) return;
    const valid = this.variantsForWidth(this.selectedWidth);
    const availableLengths = [...new Set(valid.map((variant) => variant.options[this.lengthOption]))];
    [...select.options].forEach((option) => {
      option.hidden = option.value !== '' && !availableLengths.includes(option.value);
      option.disabled = option.value !== '' && !availableLengths.includes(option.value);
    });
    if (!availableLengths.includes(this.selectedLength)) this.selectedLength = availableLengths[0] || '';
    select.value = this.selectedLength;
  }

  selectedVariant() {
    if (this.dimensionNumber(this.selectedLength) < this.dimensionNumber(this.selectedWidth)) return null;
    return this.data.variants.find((variant) => {
      const matchesShape = this.shapeOption < 0 || variant.options[this.shapeOption] === this.selectedShape;
      return matchesShape && variant.options[this.widthOption] === this.selectedWidth && variant.options[this.lengthOption] === this.selectedLength;
    });
  }

  trussQuantity() {
    const length = this.dimensionNumber(this.selectedLength);
    return length > 0 ? (length / 4) + 1 : 0;
  }

  selectedTrussAddOn() {
    if (this.selectedTruss !== 'every-hoop' || !this.data.twoPointTruss) return null;
    return { ...this.data.twoPointTruss, quantity: this.trussQuantity() };
  }

  isManualSideRollUp(product) {
    const identifier = `${product?.packageType || ''} ${product?.title || ''}`.toLowerCase();
    return identifier.includes('manual-side-roll-up') || identifier.includes('manual side roll-up');
  }

  isPoweredFans(product) {
    const identifier = `${product?.packageType || ''} ${product?.title || ''}`.toLowerCase();
    return identifier.includes('powered-fans') || identifier.includes('powered fans');
  }

  isFreshAirIntake(product) {
    const identifier = `${product?.packageType || ''} ${product?.title || ''}`.toLowerCase();
    return identifier.includes('fresh-air-intake') || identifier.includes('fresh air intake');
  }

  ensureFanSelection(choice) {
    const select = choice?.querySelector('[data-fan-size-selector]');
    if (!select) return;
    const selectedOption = [...select.options].find((option) => !option.disabled && Number(option.value) === this.selectedFanVariantId)
      || [...select.options].find((option) => !option.disabled);
    if (!selectedOption) return;
    select.value = selectedOption.value;
    this.selectedFanVariantId = Number(selectedOption.value);
  }

  ensureIntakeSelection(choice) {
    const select = choice?.querySelector('[data-intake-size-selector]');
    if (!select) return;
    const selectedOption = [...select.options].find((option) => !option.disabled && Number(option.value) === this.selectedIntakeVariantId)
      || [...select.options].find((option) => !option.disabled);
    if (!selectedOption) return;
    select.value = selectedOption.value;
    this.selectedIntakeVariantId = Number(selectedOption.value);
  }

  poweredFansVariant(product) {
    if (!product) return null;
    const available = product.variants.filter((variant) => variant.available);
    return available.find((variant) => variant.id === this.selectedFanVariantId) || available[0] || null;
  }

  freshAirIntakeVariant(product) {
    if (!product) return null;
    const available = product.variants.filter((variant) => variant.available);
    return available.find((variant) => variant.id === this.selectedIntakeVariantId) || available[0] || null;
  }

  manualSideRollUpVariant(product) {
    const greenhouseLength = this.dimensionNumber(this.selectedLength);
    if (!product || !greenhouseLength) return null;

    return product.variants
      .filter((variant) => {
        if (!variant.available) return false;
        return Object.entries(variant.optionValues).every(([name, value]) => {
          const key = name.toLowerCase().replace(/[^a-z]/g, '');
          if (key.includes('length')) return this.dimensionNumber(value) >= greenhouseLength;
          if (key.includes('width')) return String(value).toLowerCase() === String(this.selectedWidth).toLowerCase();
          if (key.includes('family')) return String(value).toLowerCase() === String(this.data.family).toLowerCase();
          return true;
        });
      })
      .sort((first, second) => {
        const firstLength = Object.entries(first.optionValues).find(([name]) => name.toLowerCase().includes('length'))?.[1];
        const secondLength = Object.entries(second.optionValues).find(([name]) => name.toLowerCase().includes('length'))?.[1];
        return this.dimensionNumber(firstLength) - this.dimensionNumber(secondLength);
      })[0] || null;
  }

  manualSideRollUpRequiresQuote(product) {
    if (!this.isManualSideRollUp(product)) return false;
    const greenhouseLength = this.dimensionNumber(this.selectedLength);
    const supportedLengths = product.variants.map((variant) => {
      const length = Object.entries(variant.optionValues).find(([name]) => name.toLowerCase().includes('length'))?.[1];
      return this.dimensionNumber(length);
    });
    const maximumLength = Math.max(0, ...supportedLengths);
    return greenhouseLength > maximumLength;
  }

  metalEndWallVariant(product) {
    const greenhouseWidth = this.dimensionNumber(this.selectedWidth);
    if (!product || !greenhouseWidth) return null;

    return product.variants.find((variant) => {
      if (!variant.available) return false;
      const supportedWidths = Object.values(variant.optionValues).flatMap((value) =>
        (String(value).match(/[0-9]+(?:\.[0-9]+)?/g) || []).map(Number),
      );
      return supportedWidths.includes(greenhouseWidth);
    }) || null;
  }

  compatibleVariant(product, kind) {
    if (!product) return null;
    if (kind === 'endWall') return this.metalEndWallVariant(product);
    if (this.isManualSideRollUp(product)) return this.manualSideRollUpVariant(product);
    if (this.isPoweredFans(product)) return this.poweredFansVariant(product);
    if (this.isFreshAirIntake(product)) return this.freshAirIntakeVariant(product);
    const selected = { family: this.data.family, width: this.selectedWidth, length: this.selectedLength };
    const recognized = ['family', 'width', 'length'];
    return product.variants.find((variant) => {
      if (!variant.available) return false;
      return Object.entries(variant.optionValues).every(([name, value]) => {
        const key = name.toLowerCase().replace(/[^a-z]/g, '');
        const dimension = recognized.find((item) => key.includes(item));
        return !dimension || String(value).toLowerCase() === String(selected[dimension]).toLowerCase();
      });
    }) || null;
  }

  updateAddOnChoices(kind) {
    const products = kind === 'ventilation' ? this.data.ventilation : this.data.endWalls;
    this.querySelectorAll(`[data-addon-kind="${kind}"]`).forEach((choice) => {
      if (choice.dataset.previewAddon === 'true') {
        choice.hidden = false;
        return;
      }
      const product = products.find((item) => String(item.id) === choice.dataset.productId);
      if (kind === 'ventilation' && this.isPoweredFans(product)) this.ensureFanSelection(choice);
      if (kind === 'ventilation' && this.isFreshAirIntake(product)) this.ensureIntakeSelection(choice);
      const variant = this.compatibleVariant(product, kind);
      const customQuote = kind === 'ventilation' && this.manualSideRollUpRequiresQuote(product);
      choice.hidden = !variant && !customQuote;
      choice.dataset.variantId = variant?.id || '';
      choice.dataset.price = variant?.price || 0;
      choice.dataset.customQuote = String(customQuote);
      choice.dataset.matchedLength = variant
        ? Object.entries(variant.optionValues).find(([name]) => name.toLowerCase().includes('length'))?.[1] || ''
        : '';
      choice.dataset.matchedSize = kind === 'endWall' && variant
        ? Object.values(variant.optionValues)[0] || ''
        : '';
      const price = choice.querySelector('[data-addon-price]');
      if (price) price.textContent = variant ? `+${this.money(variant.price)}` : customQuote ? 'Custom quote required' : '';
    });
    if (kind === 'ventilation') {
      this.querySelectorAll('input[name="kit-ventilation"]:not([value="none"]):checked').forEach((input) => {
        if (input.closest('[data-addon-kind]')?.hidden) input.checked = false;
      });
      const none = this.querySelector('input[name="kit-ventilation"][value="none"]');
      const hasSelectedPackage = Boolean(this.querySelector('input[name="kit-ventilation"]:not([value="none"]):checked'));
      if (none) none.checked = !hasSelectedPackage;
      return;
    }
    const selectedInput = this.querySelector(`input[name="kit-end-wall"][value="${CSS.escape(this.selectedEndWall)}"]`);
    if (selectedInput?.closest('[data-addon-kind]')?.hidden) {
      const none = this.querySelector('input[name="kit-end-wall"][value="none"]');
      if (none) none.checked = true;
      this.selectedEndWall = 'none';
    }
  }

  addOnFromChoice(choice) {
    if (!choice || choice.hidden) return null;
    if (choice.dataset.previewAddon === 'true') {
      let title = choice.dataset.title;
      const kitLength = this.rollupKitLength();
      if (choice.dataset.previewType === 'manual-side-roll-up' && kitLength) title += ` — ${kitLength}′ kit`;
      return { id: null, price: 0, title, preview: true };
    }
    if (choice.dataset.customQuote === 'true') {
      return { id: null, price: 0, title: `${choice.dataset.title} — custom quote required`, customQuote: true };
    }
    if (!choice.dataset.variantId) return null;
    const matchedLength = choice.dataset.matchedLength;
    const matchedSize = choice.dataset.matchedSize;
    const selectedSize = choice.dataset.poweredFans === 'true'
      ? choice.querySelector('[data-fan-size-selector]')?.selectedOptions[0]?.textContent.split('—')[0].trim()
      : choice.dataset.freshAirIntake === 'true'
        ? choice.querySelector('[data-intake-size-selector]')?.selectedOptions[0]?.textContent.split('—')[0].trim()
        : '';
    const title = choice.dataset.title.toLowerCase().includes('manual side roll-up') && matchedLength
      ? `${choice.dataset.title} — ${matchedLength} complete two-side package`
      : matchedSize
        ? `${choice.dataset.title} — ${matchedSize}`
      : selectedSize
        ? `${choice.dataset.title} — ${selectedSize}`
        : choice.dataset.title;
    return { id: Number(choice.dataset.variantId), price: Number(choice.dataset.price), title };
  }

  selectedVentilationAddOns() {
    return [...this.querySelectorAll('input[name="kit-ventilation"]:not([value="none"]):checked')]
      .map((input) => this.addOnFromChoice(input.closest('[data-addon-kind="ventilation"]')))
      .filter(Boolean);
  }

  selectedEndWallAddOn() {
    if (this.selectedEndWall === 'none') return null;
    const choice = this.querySelector(`input[name="kit-end-wall"][value="${CSS.escape(this.selectedEndWall)}"]`)?.closest('[data-addon-kind="endWall"]');
    return this.addOnFromChoice(choice);
  }

  rollupKitLength() {
    const greenhouseLength = Number.parseFloat(this.selectedLength);
    if (!Number.isFinite(greenhouseLength) || greenhouseLength <= 0) return null;
    const availableKitLengths = [10, 15, 20, 25, 30, 35, 40, 45, 50, 65, 75, 85, 100];
    return availableKitLengths.find((length) => length >= greenhouseLength) || null;
  }

  updateVentilationPreview() {
    const kitLength = this.rollupKitLength();
    const greenhouseLength = Number.parseFloat(this.selectedLength);
    const label = kitLength
      ? `${kitLength}′ roll-up kit for a ${greenhouseLength}′ greenhouse`
      : greenhouseLength > 0
        ? 'custom quote required for lengths over 100′'
        : 'choose a greenhouse length';
    this.text('[data-rollup-kit-length]', label);
  }

  update() {
    this.querySelectorAll('[data-shape]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.shape === this.selectedShape)));
    this.querySelectorAll('[data-width]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.width === this.selectedWidth)));
    this.updateVentilationPreview();
    this.updateAddOnChoices('ventilation');
    this.updateAddOnChoices('endWall');
    const variant = this.selectedVariant();
    const ventilations = this.selectedVentilationAddOns();
    const endWall = this.selectedEndWallAddOn();
    const truss = this.selectedTrussAddOn();
    const base = variant?.price || 0;
    const hasBasePrice = base > 0;
    const coveringUpgrade = this.selectedCovering === '10-mil-string-reinforced' ? Math.round(base * 0.1) : 0;
    const ventilationTotal = ventilations.reduce((sum, ventilation) => sum + (ventilation.price || 0), 0);
    const trussTotal = (truss?.price || 0) * (truss?.quantity || 0);
    const unitTotal = base + ventilationTotal + (endWall?.price || 0) + trussTotal + coveringUpgrade;
    const total = unitTotal * this.quantity;

    this.text('[data-current-price]', hasBasePrice ? this.money(base) : 'Call/Email for Price');
    this.text('[data-base-price]', hasBasePrice ? this.money(base * this.quantity) : 'Call/Email for Price');
    this.text('[data-ventilation-price]', this.money(ventilationTotal * this.quantity));
    this.text('[data-end-wall-price]', this.money((endWall?.price || 0) * this.quantity));
    const calculatedTrussQuantity = this.trussQuantity();
    this.text('[data-truss-count]', calculatedTrussQuantity ? `${calculatedTrussQuantity} trusses` : 'choose a greenhouse length');
    this.text('[data-truss-choice-price]', calculatedTrussQuantity ? `+${this.money(this.data.twoPointTruss?.price || 0)} each × ${calculatedTrussQuantity} = ${this.money((this.data.twoPointTruss?.price || 0) * calculatedTrussQuantity)}` : 'Choose a length');
    this.text('[data-truss-price]', this.money(trussTotal * this.quantity));
    this.text('[data-covering-upgrade-price]', hasBasePrice ? this.money(coveringUpgrade * this.quantity) : 'Calculated with quote');
    this.text('[data-covering-upgrade-choice-price]', hasBasePrice ? `+${this.money(Math.round(base * 0.1))}` : 'Calculated with quote');
    this.text('[data-configured-total]', hasBasePrice ? this.money(total) : 'Call/Email for Price');
    this.text('[data-sku]', variant?.sku || '—');
    this.text('[data-availability]', hasBasePrice ? (variant?.available ? this.data.availableText : this.data.unavailableText) : 'Contact us for pricing');
    const compare = this.querySelector('[data-compare-price]');
    if (compare) {
      compare.textContent = variant?.compareAtPrice > variant?.price ? this.money(variant.compareAtPrice) : '';
      compare.hidden = !compare.textContent;
    }
    const paymentVariant = this.querySelector('[data-payment-variant]');
    if (paymentVariant && variant) {
      paymentVariant.value = variant.id;
      paymentVariant.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.updateQuoteLink(variant, ventilations, endWall, truss, coveringUpgrade, total, hasBasePrice);
    this.updatePurchaseState(variant);
    if (variant?.featuredImage) this.selectMediaByUrl(variant.featuredImage);
  }

  updatePurchaseState(variant) {
    const add = this.querySelector('[data-add-configuration]');
    const hasBasePrice = Boolean(variant && variant.price > 0);
    const ventilations = this.selectedVentilationAddOns();
    const endWall = this.selectedEndWallAddOn();
    const truss = this.selectedTrussAddOn();
    const reinforcedPlasticUpgrade = this.selectedReinforcedPlasticUpgrade(variant);
    const quoteOnlySelection = (this.selectedCovering === '10-mil-string-reinforced' && !reinforcedPlasticUpgrade?.available)
      || ventilations.some((ventilation) => ventilation.preview || ventilation.customQuote)
      || endWall?.preview
      || endWall?.customQuote;
    const missing = [];
    if (this.shapeOption >= 0 && !this.selectedShape) missing.push('a shape');
    if (!this.selectedWidth) missing.push('a width');
    if (!this.selectedLength) missing.push('a length');
    if (!this.querySelector('input[name="kit-ventilation"]:checked')) missing.push('a ventilation option');
    if (!this.querySelector('input[name="kit-end-wall"]:checked')) missing.push('an end-wall option');
    if (truss && !truss.available) missing.push('available 2-point trusses');
    if (!this.querySelector('input[name="kit-covering"]:checked')) missing.push('a greenhouse plastic option');
    if (variant && !variant.available) missing.push('an available size');
    if (!variant) missing.push('a valid width and length');
    add.disabled = missing.length > 0 || !hasBasePrice || quoteOnlySelection;
    add.textContent = hasBasePrice && !quoteOnlySelection ? 'Add to Cart' : 'Call/Email for Price';
    if (!missing.length && quoteOnlySelection) {
      this.setStatus('This selection requires a detailed quote so every upgrade is priced correctly in the order.', false);
    } else if (!missing.length && !hasBasePrice) {
      this.setStatus('This size is available by quote. Call (541) 270-2911 or email grow@sunbowgreenhouses.com.', false);
    } else {
      this.setStatus(missing.length ? `Please choose ${missing[0]}.` : 'Configuration ready to add to cart.', false);
    }
  }

  updateQuoteLink(variant, ventilations, endWall, truss, coveringUpgrade, total, hasBasePrice) {
    const link = this.querySelector('[data-quote-link]');
    if (!link) return;
    const url = new URL(link.dataset.baseUrl || '/pages/contact', window.location.origin);
    const values = {
      product_family: this.data.family,
      product_title: this.data.title,
      shape: this.selectedShape,
      width: this.selectedWidth,
      length: this.selectedLength,
      ventilation: ventilations.map((ventilation) => ventilation.title).join(' + ') || 'None',
      metal_end_wall_package: endWall?.title || 'None',
      two_point_trusses: truss ? `${truss.quantity} × ${truss.title}` : 'None',
      two_point_trusses_price: this.money((truss?.price || 0) * (truss?.quantity || 0) * this.quantity),
      greenhouse_plastic: this.selectedCovering === '10-mil-string-reinforced' ? '10 mil String Reinforced Plastic Upgrade' : 'Standard 6 mil Greenhouse Plastic',
      greenhouse_plastic_upgrade_price: this.money(coveringUpgrade),
      configured_price: hasBasePrice ? this.money(total) : 'Call/Email for Price',
      product_url: window.location.href.split('?')[0],
      variant: variant?.id || '',
    };
    Object.entries(values).forEach(([key, value]) => url.searchParams.set(key, value));
    link.href = url.toString();
  }

  setQuantity(value) {
    this.quantity = Math.max(1, Math.min(99, Number.isFinite(value) ? value : 1));
    const input = this.querySelector('[data-quantity]');
    if (input) input.value = this.quantity;
    this.update();
  }

  async addToCart(event) {
    const variant = this.selectedVariant();
    if (!variant?.available) return this.setStatus('That size is not currently available.', true);
    if (!(variant.price > 0)) return this.setStatus('Please call (541) 270-2911 or email grow@sunbowgreenhouses.com for pricing on this size.', false);
    const selectedVentilations = this.selectedVentilationAddOns();
    const selectedEndWall = this.selectedEndWallAddOn();
    const selectedTruss = this.selectedTrussAddOn();
    const reinforcedPlasticUpgrade = this.selectedReinforcedPlasticUpgrade(variant);
    if (selectedTruss && !selectedTruss.available) return this.setStatus('The 2-point trusses are not currently available.', true);
    if ((this.selectedCovering === '10-mil-string-reinforced' && !reinforcedPlasticUpgrade?.available) || selectedVentilations.some((ventilation) => ventilation.preview || ventilation.customQuote) || selectedEndWall?.preview || selectedEndWall?.customQuote) {
      return this.setStatus('This selection requires a detailed quote so every upgrade is priced correctly in the order.', false);
    }
    const button = event.currentTarget;
    button.disabled = true;
    button.textContent = 'Adding…';
    const ventilations = selectedVentilations;
    const endWall = selectedEndWall;
    const coveringUpgrade = this.selectedCovering === '10-mil-string-reinforced' ? Math.round((variant.price || 0) * 0.1) : 0;
    const configurationId = `SB-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    const shared = {
      'Configuration': configurationId,
      'Greenhouse family': this.data.family,
      'Width': this.selectedWidth,
      'Length': this.selectedLength,
      'Ventilation': ventilations.map((ventilation) => ventilation.title).join(' + ') || 'None',
      'Metal end-wall package': endWall?.title || 'None',
      'Greenhouse plastic': this.selectedCovering === '10-mil-string-reinforced' ? '10 mil String Reinforced Plastic Upgrade' : 'Standard 6 mil Greenhouse Plastic',
      'Plastic upgrade amount': this.money(coveringUpgrade),
      '_Sunbow configuration ID': configurationId,
    };
    if (this.selectedShape) shared.Shape = this.selectedShape;
    if (selectedTruss) shared['2-point trusses'] = `${selectedTruss.quantity} trusses (one per hoop)`;
    const items = [{ id: Number(variant.id), quantity: this.quantity, properties: shared }];
    if (this.selectedCovering === '10-mil-string-reinforced') items.push({
      id: Number(reinforcedPlasticUpgrade.id),
      quantity: this.quantity,
      properties: {
        'Configuration': configurationId,
        'For': `${this.data.title} — ${this.selectedWidth} × ${this.selectedLength}`,
        '_Sunbow configuration ID': configurationId,
        '_Sunbow parent variant': String(variant.id),
      },
    });
    [...ventilations, endWall].filter((addon) => addon?.id).forEach((addon) => items.push({
      id: addon.id,
      quantity: this.quantity,
      properties: {
        'Configuration': configurationId,
        'For': `${this.data.title} — ${this.selectedWidth} × ${this.selectedLength}`,
        '_Sunbow configuration ID': configurationId,
        '_Sunbow parent variant': String(variant.id),
      },
    }));
    if (selectedTruss?.id) items.push({
      id: Number(selectedTruss.id),
      quantity: this.quantity * selectedTruss.quantity,
      properties: {
        'Configuration': configurationId,
        'For': `${this.data.title} — ${this.selectedWidth} × ${this.selectedLength}`,
        'Placement': "One 2-point 5' truss on every hoop",
        '_Sunbow configuration ID': configurationId,
        '_Sunbow parent variant': String(variant.id),
      },
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId);
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'The configuration could not be added.');
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'greenhouse-kit-configurator', sourceId: this.id, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections: result.sections, didError: false },
      });
      this.setStatus('Your complete greenhouse configuration was added to the cart.', false);
      button.textContent = 'Added to Cart';
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
      this.setStatus(error.message, true);
      button.textContent = 'Add to Cart';
      button.disabled = false;
    } finally {
      window.setTimeout(() => {
        button.textContent = 'Add to Cart';
        this.updatePurchaseState(this.selectedVariant());
      }, 1800);
    }
  }

  selectMedia(button) {
    this.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.setAttribute('aria-current', 'false'));
    button.setAttribute('aria-current', 'true');
    const image = this.querySelector('[data-primary-image]');
    if (!image) return;
    image.src = button.dataset.imageUrl;
    image.srcset = '';
    image.alt = button.dataset.imageAlt || this.data.title;
  }

  selectedReinforcedPlasticUpgrade(variant = this.selectedVariant()) {
    if (!variant?.price) return null;
    const upgradePrice = Math.round(variant.price * 0.1);
    return (this.data.reinforcedPlasticUpgrades || []).find((upgrade) => upgrade.price === upgradePrice) || null;
  }

  changeMedia(direction) {
    const thumbnails = [...this.querySelectorAll('[data-media-thumb]')];
    if (thumbnails.length < 2) return;
    const currentIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
    const nextIndex = (Math.max(currentIndex, 0) + direction + thumbnails.length) % thumbnails.length;
    this.selectMedia(thumbnails[nextIndex]);
    thumbnails[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  selectMediaByUrl(url) {
    const match = [...this.querySelectorAll('[data-media-thumb]')].find((button) => button.dataset.imageUrl.includes(url.split('?')[0]));
    if (match) this.selectMedia(match);
  }

  money(cents) {
    return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format((Number(cents) || 0) / 100);
  }

  text(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  setStatus(message, error) {
    const status = this.querySelector('[data-kit-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', Boolean(error));
  }
}

if (!customElements.get('greenhouse-kit-configurator')) customElements.define('greenhouse-kit-configurator', GreenhouseKitConfigurator);
